
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from "react";
import { useWallet } from "../../hooks/useWallet";
import { X, Wallet, ArrowRight, Loader2, Smartphone, QrCode, Sparkles, ArrowLeft } from "lucide-react";
import { WalletType } from "../../types";
import { WalletIcon } from "../ui/ChainIcons";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from "../ui/Button";

export const WalletConnectModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { connect, isConnecting, isPairing } = useWallet();
  const [showQR, setShowQR] = useState(false);

  const handleConnect = async (type: WalletType) => {
      if (type === 'walletconnect') {
          setShowQR(true);
          // Auto-resolve pairing after a realistic delay for demo purposes
          setTimeout(async () => {
              await connect(type);
              onClose();
          }, 3000);
      } else {
          await connect(type);
          onClose();
      }
  }

  const universalWallets: { id: WalletType; name: string; chain: string; recommended?: boolean; mobile?: boolean }[] = [
      { id: 'walletconnect', name: 'WalletConnect', chain: 'Any Mobile Wallet', recommended: true, mobile: true },
  ];

  const directWallets: { id: WalletType; name: string; chain: string; mobile?: boolean }[] = [
      { id: 'phantom', name: 'Phantom', chain: 'Solana', mobile: true },
      { id: 'suiet', name: 'Suiet', chain: 'Sui' },
      { id: 'solflare', name: 'Solflare', chain: 'Solana', mobile: true },
      { id: 'base', name: 'Base Wallet', chain: 'Base', mobile: true },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-stone-900/80 backdrop-blur-md animate-fade-in">
      <motion.div 
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white dark:bg-[#15171C] rounded-[32px] shadow-2xl w-full max-w-md overflow-hidden border border-stone-200 dark:border-[#2D3142] relative"
      >
         <div className="p-8 border-b border-stone-100 dark:border-white/5 flex justify-between items-center bg-gray-50/50 dark:bg-white/[0.01]">
            <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-nobel-gold text-white flex items-center justify-center shadow-lg shadow-nobel-gold/20">
                    {showQR ? <QrCode size={24} /> : <Wallet size={24} />}
                </div>
                <div>
                    <h2 className="font-serif text-2xl text-stone-900 dark:text-white font-bold tracking-tight">
                        {showQR ? 'Pairing Active' : 'Access Vault'}
                    </h2>
                    <p className="text-[10px] text-stone-400 uppercase font-bold tracking-widest mt-0.5">Sovereign Connection</p>
                </div>
            </div>
            <button onClick={onClose} className="p-2 text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors bg-white dark:bg-white/5 rounded-full border border-stone-100 dark:border-white/10">
                <X size={20} />
            </button>
        </div>

        <div className="p-8 space-y-8 max-h-[70vh] overflow-y-auto no-scrollbar">
            <AnimatePresence mode="wait">
                {showQR ? (
                    <motion.div 
                        key="qr-view"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="flex flex-col items-center text-center space-y-6"
                    >
                        <div className="p-5 bg-white rounded-[32px] shadow-2xl border border-stone-100 relative group overflow-hidden">
                            <QRCodeCanvas value="wc:ghost-pairing-id-402-v1" size={200} bgColor="#ffffff" fgColor="#0A0B0F" level="H" />
                            {isConnecting && (
                                <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex flex-col items-center justify-center">
                                    <Loader2 className="animate-spin text-nobel-gold mb-3" size={40} />
                                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#0A0B0F]">Synchronizing Ledger</span>
                                </div>
                            )}
                        </div>
                        <div className="space-y-2">
                            <p className="text-sm text-stone-600 dark:text-stone-300 font-medium">Scan with your mobile wallet app</p>
                            <p className="text-[10px] text-stone-400 uppercase tracking-widest leading-relaxed max-w-[240px] mx-auto">Establishing encrypted bridge via Ghost-Relay nodes.</p>
                        </div>
                        <button 
                            onClick={() => setShowQR(false)} 
                            className="flex items-center gap-2 text-xs font-bold text-stone-400 hover:text-stone-600 transition-colors"
                        >
                            <ArrowLeft size={14} /> Use another method
                        </button>
                    </motion.div>
                ) : (
                    <motion.div 
                        key="list-view"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className="space-y-8"
                    >
                        {/* Universal / WalletConnect Section */}
                        <div className="space-y-3">
                            <div className="text-[10px] font-bold text-stone-400 uppercase tracking-[0.2em] px-1">Universal Access</div>
                            {universalWallets.map((w) => (
                                <button 
                                    key={w.id}
                                    onClick={() => handleConnect(w.id)}
                                    disabled={isConnecting}
                                    className="w-full group flex items-center justify-between p-5 rounded-2xl border-2 border-nobel-gold/30 bg-nobel-gold/5 hover:bg-nobel-gold/10 hover:border-nobel-gold transition-all duration-300 relative overflow-hidden"
                                >
                                    <div className="absolute top-0 right-0 p-2 opacity-10">
                                        <QrCode size={48} />
                                    </div>
                                    <div className="flex items-center gap-4 relative z-10">
                                        <WalletIcon wallet={w.id} className="w-12 h-12 rounded-xl shadow-md" />
                                        <div className="text-left">
                                            <div className="flex items-center gap-2">
                                                <div className="font-bold text-stone-900 dark:text-white text-lg">Mobile & Browser</div>
                                                <div className="bg-nobel-gold text-[8px] font-black text-white px-1.5 py-0.5 rounded uppercase flex items-center gap-1"><Sparkles size={8} fill="currentColor"/> Recommended</div>
                                            </div>
                                            <div className="text-xs text-stone-500 font-medium">Scan to connect any mobile wallet</div>
                                        </div>
                                    </div>
                                    {isConnecting ? <Loader2 className="animate-spin text-nobel-gold" size={20}/> : <ArrowRight size={20} className="text-nobel-gold group-hover:translate-x-1 transition-transform" />}
                                </button>
                            ))}
                        </div>

                        {/* Direct Wallet Options */}
                        <div className="space-y-3">
                            <div className="text-[10px] font-bold text-stone-400 uppercase tracking-[0.2em] px-1">Browser Extensions</div>
                            <div className="grid grid-cols-1 gap-2">
                                {directWallets.map((w) => (
                                    <button 
                                        key={w.id}
                                        onClick={() => handleConnect(w.id)}
                                        disabled={isConnecting}
                                        className="w-full group flex items-center justify-between p-4 rounded-xl border border-stone-200 dark:border-white/10 bg-white dark:bg-white/5 hover:border-nobel-gold/50 hover:bg-stone-50 dark:hover:bg-white/[0.08] transition-all"
                                    >
                                        <div className="flex items-center gap-4">
                                            <WalletIcon wallet={w.id} className="w-10 h-10 rounded-lg grayscale group-hover:grayscale-0 transition-all" />
                                            <div className="text-left">
                                                <div className="flex items-center gap-2">
                                                    <div className="font-bold text-stone-800 dark:text-gray-200 text-sm">{w.name}</div>
                                                    {w.mobile && (
                                                        <div className="text-[8px] font-bold text-stone-400 dark:text-stone-500 flex items-center gap-1 border border-stone-200 dark:border-white/5 px-1.5 py-0.5 rounded"><Smartphone size={8}/> Mobile</div>
                                                    )}
                                                </div>
                                                <div className="text-[10px] text-stone-500">{w.chain}</div>
                                            </div>
                                        </div>
                                        {isConnecting ? <Loader2 className="animate-spin text-stone-400" size={16}/> : <ArrowRight size={16} className="text-stone-300 dark:text-stone-700 group-hover:text-nobel-gold transition-colors" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
        
        <div className="p-6 bg-stone-50 dark:bg-black/20 text-center border-t border-stone-100 dark:border-white/5">
            <p className="text-[10px] text-stone-400 font-bold uppercase tracking-[0.2em] flex items-center justify-center gap-2">
                <ShieldCheck className="w-3 h-3 text-emerald-500" /> AES-256 Encrypted Connection
            </p>
        </div>
      </motion.div>
    </div>
  );
};

const ShieldCheck = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/><path d="m9 12 2 2 4-4"/></svg>
);
